import {
  Box,
  Button,
  Colors,
  IcMinus,
  IcPlus,
  Text
} from '@fozito/common-ui';
import { Form } from 'antd';
import cashierService from 'modules/POS/services';
import { useAppDispatch } from 'store/hooks';
import styled from 'styled-components';
import { formatCurrency } from 'utils/formatCurrency';
import { IUISelectItem } from '../cashier.interface';
import { calcDiscountForItem } from '../cashier.utils';
import { globalModalEditPriceItemRef } from '../features/AddPrice/EditPrice';
import { globalDiscountByItemRef } from '../features/Discount/DiscountModalV2/DiscountByItem';
import { globalModalUpdateQuantityRef } from '../features/UpdateQuantity';
import remove_icon from './close.svg';
import discount_active_icon from './discount-active.svg';
import discount_icon from './discount.svg';
import remove_active_icon from './remove-active.svg';

const Container = styled(Box)`
  padding: 2px 16px;
  padding-right: 8px;
  border-bottom: 1px solid ${Colors.neutral.border[2]};
  margin-right: 8px;
`;

const ItemName = styled(Text)`
  flex: 1;
  color: ${Colors.neutral.text[5]};
  font-weight: 500;
  margin-right: 16px;
`;

const Price = styled(Text)`
  text-align: right;
  color: ${Colors.neutral.text[5]};
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: right ;
  gap: 8px;
  padding-right: 8px;
`;

const Discount = styled(Text)`
  min-width: 80px;
  text-align: right;
  color: ${Colors.primary[6]};
  text-decoration: line-through;
  font-weight: 600;
`;

const QuantityContainer = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 125px;
`;

const QuantityButton = styled(Button)`
  min-width: 40px;
  height: 38px;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${Colors.neutral.border[2]} !important;
  box-shadow: unset;
  position: relative;

  svg {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }
`;

const QuantityDisplay = styled(Button)`
  flex:1;
  height: 40px;
  font-weight: 500;
`;

const ActionButton = styled(Button)`
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
  width: 32px;

  &:hover,
  &:active {
    opacity: 0.8;
    transform: scale(1.2);
    transition: all 0.1s;
  }

  &:disabled {
    opacity: 0.7;
    &:hover,
    &:active {
      opacity: 1;
      cursor: not-allowed;
      transform: none;
      transition: none;
    }
  }
`;

type Props = {
  index: number;
  value?: IUISelectItem;
  onChange?: (value: IUISelectItem) => void;
  remove?: () => void;
  name?: any;
  activeItemLatestId?: string | null;
};

const BillSelectionItem = ({ name, value, remove, onChange = () => undefined, activeItemLatestId }: Props) => {
  const dispatch = useAppDispatch();
  const active = activeItemLatestId === value?.uuid;
  const trigger = () => dispatch(cashierService.actions.setActiveItemLatest(value?.uuid || ''));
  const handleOnChange = (value: IUISelectItem) => {
    onChange(value);
    trigger();
  };
  return (
    <Container
      background={active ? 'black' : ''}
      borderRadius={active ? 8 : 0}
    >
      <Box display='flex' alignItems='center' textAlign='left'
        gridTemplateColumns={'50px auto auto 168px 70px'}>
        <Box flex={1} display='flex' flexDirection='column'>
          <ItemName content_content_2_16_medium style={active ? { color: '#fff' } : {}}>{value?.itemName}</ItemName>
          {!!value?.requiredMember && <Text content_content_1_16_regular style={{ color: '#FF7D00' }}>Store Member Only</Text>}
        </Box>
        <Form.Item name={[name, 'quantity']} noStyle>
          <Quantity active={active} data={value} onChange={trigger} />
        </Form.Item>
        <Box display='flex' alignItems='center' textAlign='left' flex={1} justifyContent='flex-end'>
          <ItemPriceEditor active={active} data={value} onChange={handleOnChange} />
          <Box display='flex' gap={0}>
            <DiscountByItem active={active} data={value} onChange={handleOnChange} />
            <ActionButton type_ghost onClick={remove}>
              <img src={active ? remove_active_icon : remove_icon} alt='remove_icon' style={{ height: 28, width: 'auto' }} />
            </ActionButton>
          </Box>
        </Box>
      </Box>
    </Container>
  );
};
type ItemPriceProps = {
  onChange?: (value: IUISelectItem) => void;
  data?: IUISelectItem;
  active?: boolean;
};

const ItemPriceEditor = ({
  data,
  onChange = () => undefined,
  active,
}: ItemPriceProps) => {
  const amount = (data?.quantity || 0) * (data?.price || 0);
  const discount = data ? calcDiscountForItem(data) : 0;

  const amountFinal = amount - discount;
  const onClick = () => data && globalModalEditPriceItemRef.current?.open(data, (_, price) => {
    onChange({ ...data, itemId: 'QUICK_PAY_ID', activeTax: data.activeTax, price: price });
  });
  return (
    <Box display='flex' alignItems='center' textAlign='left' justifyContent='flex-end' minWidth={175} marginLeft={8} >
      {discount ?
        <Price onClick={onClick} content_content_3_16_semibold style={active ? { color: '#fff' } : {}}>
          <Discount content_content_3_16_semibold>{formatCurrency(amount)}</Discount>
          {formatCurrency(amountFinal > 0 ? amountFinal : 0)}
        </Price>
        : <Price onClick={onClick} content_content_3_16_semibold style={active ? { color: '#fff' } : {}}>
          {formatCurrency(amount > 0 ? amount : 0)}
        </Price>}
    </Box>
  );
};

type DiscountByItemProps = {
  onChange?: (value: IUISelectItem) => void;
  data?: IUISelectItem;
  active?: boolean;
};

const DiscountByItem = ({
  data,
  onChange = () => undefined,
  active,
}: DiscountByItemProps) => {
  const onDiscount = () => data && globalDiscountByItemRef.current?.open(data, (_, discount) => {
    onChange({ ...data, discountSetting: discount });
  });
  return (
    <ActionButton type_ghost onClick={onDiscount}>
      <img src={active ? discount_active_icon : discount_icon} alt='remove_icon' style={{ height: 28, width: 'auto' }} />
    </ActionButton>
  );
};


type QuantityProps = {
  value?: number;
  onChange?: (value: number) => void;
  data?: IUISelectItem;
  active?: boolean;
};

const Quantity = ({
  data,
  value = 0,
  onChange = () => undefined,
  active,
}: QuantityProps) => {
  const onUpdateQuantity = () =>
    data &&
    globalModalUpdateQuantityRef.current?.open(data, (_, quantity) => {
      onChange(quantity);
    });


  return (
    <QuantityContainer>
      <QuantityButton
        disabled={value <= 0}
        onClick={() => (value > 0 ? onChange(value - 1) : undefined)}
        type_ghost
        style={active ? { background: '#fff' } : {}}
      >
        <IcMinus />
      </QuantityButton>

      <QuantityDisplay type_ghost onClick={onUpdateQuantity} style={{ padding: '0' }}>
        <Text content_content_3_16_semibold style={active ? { color: '#fff' } : {}}>
          {value || 0}
        </Text>
      </QuantityDisplay>

      <QuantityButton onClick={() => onChange(value + 1)} type_ghost
        style={active ? { background: '#fff' } : {}}
      >
        <IcPlus />
      </QuantityButton>
    </QuantityContainer>
  );
};

export default BillSelectionItem;
